/**
 * What the platform was doing while people used it — batch, and the front door.
 *
 * Two things this console never showed, both of which were already in the
 * workspace:
 *
 * **Batch.** 97 % of this environment's exceptions come from batch jobs rather
 * than from anybody's screen, and `AppExceptions` has always carried the job
 * caption alongside the class and method. The column that earns its place is
 * `steady`: a job failing 928 times an hour, unchanged at three in the morning,
 * is a retry loop — and a loop is the kind of fault nobody notices precisely
 * because it never spikes. Ranked by errors, with the loops badged, because the
 * question is "what is broken" rather than "what was worst for one hour".
 *
 * **The front door.** Microsoft's own synthetic tests have been hitting this
 * environment from a dozen Azure locations every few minutes for as long as it
 * has existed. Put beside the hours people suffered, they answer "was it them
 * or was it us" for that hour.
 *
 * That comparison used to be two numbers in adjacent table cells, which is
 * exactly the shape a reader skims past: an hour where users saw a p95 of
 * 8 797 ms sat next to a probe p50 of 217 ms and nothing about the row said
 * *forty times*. So the hours are **paired bars on one shared millisecond
 * axis** — felt long, probe short — and the ratio is no longer something the
 * reader has to compute. The number stays printed beside each bar, because the
 * bar is the comparison and the number is the fact; the full row, users and
 * loads included, is one click away in the card's table twin.
 *
 * Two honesty properties the chart has to carry and the table did not:
 *
 * * **A failed probe is not a fast probe.** An hour with failed runs paints its
 *   probe bar in the reserved danger tone, says how many failed in the label
 *   beside it, and is named again under the chart with an icon. A short bar and
 *   a broken probe must never look alike.
 * * **A missing measurement is not a zero.** A null p50 or p95 draws no bar at
 *   all and is named in its own sentence under the chart. Nothing is
 *   interpolated and nothing is floored to zero.
 *
 * The caveat is rendered as prominently as the numbers and comes from the
 * payload rather than from this file: the probe is a URL ping that follows
 * redirects and never signs in, so a green result rules out the network path
 * and the front door and says nothing whatever about the application tier. A
 * reader who takes this chart for "F&O was healthy" has been misled by the
 * panel, which is precisely why the sentence sits above it.
 *
 * **L1 per the design rules.** One ranked table, one ranked chart and one
 * reference table. The chart's door is its table twin — the same twelve hours
 * with the users and the loads the bars cannot carry, opened by `View data` and
 * held in the URL. Beyond that there is nothing to drill to: the detail behind
 * a batch job, and behind an hour, lives in F&O rather than in this payload, so
 * no aggregate here pretends otherwise.
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { Activity, AlertTriangle, Globe2, Icon, Repeat } from "@norican/icons";
import {
  Alert,
  Badge,
  chartColors,
  DataGrid,
  EmptyState,
  Tooltip,
  Typography,
} from "@norican/react";

import { ChartCard } from "@/components/board";
import type {
  FoConsoleAvailabilityLocation,
  FoConsoleBatchJob,
  FoConsoleContrastHour,
  FoConsoleLookback,
  FoConsolePlatform,
} from "@/lib/api";

import { formatDurationCompact, formatDurationMs, lookbackOption } from "./console-format";
import { PanelCard } from "./panel-card";
import { formatBinDateTime, formatCount } from "./status";

interface PlatformSectionProps {
  platform: FoConsolePlatform | undefined;
  lookback: FoConsoleLookback;
}

/** One decimal, so 928.3 errors an hour reads as a rate rather than a count. */
function formatRate(perHour: number): string {
  return `${perHour.toLocaleString(undefined, { maximumFractionDigits: 1 })}/h`;
}

/** A share as a whole percent — 0.9745 → "97 %". */
function formatShare(part: number, whole: number): string {
  if (whole <= 0) {
    return "—";
  }
  return `${Math.round((part / whole) * 100)} %`;
}

export function PlatformSection({ platform, lookback }: PlatformSectionProps) {
  const windowCaption = lookbackOption(lookback).caption;
  const jobs = platform?.batch?.jobs_listed ?? [];
  const availability = platform?.availability ?? [];
  const contrast = platform?.contrast ?? [];
  const loops = jobs.filter((job) => job.steady).length;

  return (
    <section className="flex flex-col gap-6">
      <BatchPanel
        jobs={jobs}
        totalErrors={platform?.batch?.total_errors ?? 0}
        batchErrors={platform?.batch?.batch_errors ?? 0}
        distinctJobs={platform?.batch?.jobs ?? 0}
        loops={loops}
        windowCaption={windowCaption}
      />
      <AvailabilityPanel
        locations={availability}
        contrast={contrast}
        caveat={platform?.probe_caveat ?? ""}
        windowCaption={windowCaption}
      />
    </section>
  );
}

/* ---------------------------------------------------------------- batch --- */

interface BatchPanelProps {
  jobs: FoConsoleBatchJob[];
  totalErrors: number;
  batchErrors: number;
  distinctJobs: number;
  loops: number;
  windowCaption: string;
}

function BatchPanel({
  jobs,
  totalErrors,
  batchErrors,
  distinctJobs,
  loops,
  windowCaption,
}: BatchPanelProps) {
  if (jobs.length === 0) {
    return (
      <PanelCard title="Batch jobs that are failing" caption={windowCaption}>
        <EmptyState
          icon={<Icon icon={Activity} size="lg" aria-hidden="true" />}
          size="sm"
          title="No batch job threw in this window"
          description="The batch query answered and returned no row. A query that failed would be named in the errors above instead, so this is a quiet window rather than a broken panel."
        />
      </PanelCard>
    );
  }

  const share = formatShare(batchErrors, totalErrors);
  const caption =
    `${formatCount(distinctJobs)} jobs threw · ${formatCount(batchErrors)} of ${formatCount(totalErrors)} ` +
    `exceptions (${share}) came from batch, not from anybody's screen · ${windowCaption}`;

  return (
    <PanelCard title="Batch jobs that are failing" caption={caption} padding="none">
      {loops > 0 ? (
        <div className="px-4 pt-4">
          <Alert
            tone="warning"
            title={`${formatCount(loops)} ${loops === 1 ? "job is" : "jobs are"} failing steadily, hour after hour`}
          >
            A steady rate through the night is a retry loop rather than a bad afternoon — the kind
            of fault that never spikes and so is never noticed. The rate is errors per hour it was
            failing in, not per hour of the window.
          </Alert>
        </div>
      ) : null}
      <div className="h-[26rem]">
        {/* DataGrid needs a definite height for its sticky header and scroller. */}
        <DataGrid<FoConsoleBatchJob>
          data={jobs}
          getRowId={(row) => row.job}
          density="compact"
          aria-label="Batch jobs throwing exceptions, worst first"
          getRowSearchText={(row) => `${row.job} ${row.signature}`}
          columns={[
            {
              id: "job",
              header: "Batch job",
              accessor: (row) => row.job,
              sortable: true,
              cell: (row) => (
                <div className="flex flex-wrap items-center gap-2">
                  <Typography variant="body-sm" className="font-medium">
                    {row.job}
                  </Typography>
                  {row.steady ? <LoopBadge job={row.job} /> : null}
                </div>
              ),
            },
            {
              id: "errors",
              header: "Errors",
              accessor: (row) => row.errors,
              sortable: true,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.errors)}</span>,
            },
            {
              id: "rate",
              header: "Rate",
              accessor: (row) => row.per_hour,
              sortable: true,
              align: "end",
              cell: (row) => (
                <span className="text-fg-muted tabular-nums">{formatRate(row.per_hour)}</span>
              ),
            },
            {
              id: "hours",
              header: "Hours failing",
              accessor: (row) => row.hours,
              sortable: true,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.hours)}</span>,
            },
            {
              id: "companies",
              header: "Companies",
              accessor: (row) => row.companies,
              sortable: true,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.companies)}</span>,
            },
            {
              id: "signature",
              header: "Fault",
              accessor: (row) => row.signature,
              sortable: true,
              cell: (row) =>
                row.signature ? (
                  <code className="text-fg-muted text-xs">{row.signature}</code>
                ) : (
                  <span className="text-fg-muted">—</span>
                ),
            },
          ]}
        />
      </div>
    </PanelCard>
  );
}

/** The badge that marks a retry loop, with the reason a reader needs to trust it. */
function LoopBadge({ job }: { job: string }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger
        className="text-fg-muted inline-flex rounded focus-visible:outline-2"
        aria-label={`${job} is failing at a steady rate across the window`}
      >
        <Badge tone="warning" variant="outline" size="xs" shape="rounded">
          <span className="inline-flex items-center gap-1">
            <Icon icon={Repeat} size="xs" aria-hidden="true" />
            loop
          </span>
        </Badge>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Positioner side="top">
          <Tooltip.Popup>
            <span className="block max-w-xs text-xs">
              Failing at a steady rate across the whole window, night as well as day. That is a
              retry loop rather than a bad afternoon, and it is the kind of fault nobody notices
              because it never spikes.
            </span>
          </Tooltip.Popup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

/* --------------------------------------------------------- front door --- */

/**
 * Series keys are the row's own field names, deliberately: `Chart.LegendContent`
 * and `Chart.TooltipContent` resolve labels and colours from the container
 * config **by `dataKey`**, so a config keyed on anything else renders a legend
 * of raw field names.
 */
const FELT_KEY = "user_p95_ms";
const PROBE_KEY = "probe_p50_ms";

/**
 * The tone a probe bar wears in an hour where synthetic runs failed. A status
 * colour, and legitimately so (`dashboard-design.md` §3.2): a probe that did
 * not answer is *broken*, not "series 3". It is never colour-alone — the label
 * beside the bar carries the failure count and the note under the chart names
 * the hours with an icon.
 */
const PROBE_FAILED_KEY = "probe_failed";

/**
 * A fixed pair from the NEUTRAL half of the DS categorical ramp
 * (`--norican-chart-6…16`, the half `identityConfig` defaults to — never the
 * business-unit colours at 1…5), but not that helper's first two entries: the
 * ramp's leading neutral is a magenta close enough to the danger token that a
 * failed probe bar and the felt bar beside it could be mistaken for each other,
 * which would defeat the panel. Blue and ochre are unmistakable against red and
 * against one another, and the assignment is fixed rather than derived from
 * payload order, so a quiet window cannot repaint the series.
 */
const FELT_COLOUR = chartColors[12]; // --norican-chart-13
const PROBE_COLOUR = chartColors[6]; // --norican-chart-7

const CONTRAST_CONFIG: ChartConfig = {
  [FELT_KEY]: { label: "Felt by users (p95)", color: FELT_COLOUR },
  [PROBE_KEY]: { label: "Synthetic probe (p50)", color: PROBE_COLOUR },
  [PROBE_FAILED_KEY]: { label: "Probe failed in this hour", color: "var(--norican-danger)" },
};

/** Board-unique id for the chart's table twin — lands in the URL as `?data=…`. */
const CONTRAST_TWIN_ID = "fo-front-door-hours";

/**
 * The reading key. It was the table's caption and it is the chart's, because it
 * is the whole point of putting the two bars on one axis.
 */
const CONTRAST_RULE =
  "A slow hour whose probe was healthy is an application finding. A slow hour whose probe was " +
  "also slow, or failed, is not.";

/** What a measured-but-tiny probe bar must still show, so it cannot read as absent. */
const MIN_BAR_PX = 2;

/** One hour, ready to draw: the payload's own fields plus the derived labels. */
interface ContrastRow {
  /** Row id and twin key — the ISO hour. */
  id: string;
  /** The hour, plain, for the table twin — `20 Aug 14:00`. */
  label: string;
  /**
   * The axis category. Same hour, plus a marker when the probe returned no
   * timing at all: Recharts draws nothing for a null value and therefore no
   * end-of-bar label either, so without this the row with the *worst* probe
   * result would be the one row the chart says nothing about.
   */
  axis_label: string;
  users: number;
  events: number;
  user_p95_ms: number | null;
  probe_p50_ms: number | null;
  probe_failures: number;
  /** Config key the probe bar is coloured from; always present in the config. */
  probe_tone: typeof PROBE_KEY | typeof PROBE_FAILED_KEY;
  felt_label: string;
  probe_label: string;
}

/** The probe's number beside its bar, with the failure count folded in. */
function probeLabel(probe: number | null, failures: number): string {
  if (probe == null) {
    return failures > 0 ? `no timing · ${formatCount(failures)} failed` : "not measured";
  }
  return failures > 0
    ? `${formatDurationMs(probe)} · ${formatCount(failures)} failed`
    : formatDurationMs(probe);
}

/**
 * The axis category for one hour.
 *
 * A null `probe_p50_ms` draws no bar, and Recharts draws no end-of-bar label
 * for a bar it did not draw — so an hour where every probe run failed would be
 * the one row the chart said nothing at all about, which is the exact reading
 * this panel exists to prevent. The state goes on the axis instead, where it
 * cannot be dropped.
 */
function axisLabel(hour: FoConsoleContrastHour): string {
  const when = formatBinDateTime(hour.hour);
  if (hour.probe_p50_ms != null) {
    return when;
  }
  return hour.probe_failures > 0
    ? `${when} · probe failed, no timing`
    : `${when} · probe not measured`;
}

/**
 * Payload rows as chart rows, worst hour first.
 *
 * The backend already orders by `user_p95_ms desc`; sorting again is defensive
 * and idempotent, and it keeps an unmeasured hour at the bottom rather than
 * letting a null sort to the top as if it were the worst.
 */
function contrastRows(contrast: FoConsoleContrastHour[]): ContrastRow[] {
  return [...contrast]
    .sort((a, b) => (b.user_p95_ms ?? -1) - (a.user_p95_ms ?? -1))
    .map((hour) => ({
      id: hour.hour,
      label: formatBinDateTime(hour.hour),
      axis_label: axisLabel(hour),
      users: hour.users,
      events: hour.events,
      user_p95_ms: hour.user_p95_ms ?? null,
      probe_p50_ms: hour.probe_p50_ms ?? null,
      probe_failures: hour.probe_failures,
      probe_tone: hour.probe_failures > 0 ? PROBE_FAILED_KEY : PROBE_KEY,
      felt_label: hour.user_p95_ms == null ? "not measured" : formatDurationMs(hour.user_p95_ms),
      probe_label: probeLabel(hour.probe_p50_ms ?? null, hour.probe_failures),
    }));
}

/**
 * Plot height from the row count, returned as whole Tailwind classes rather
 * than assembled from a number: the scanner only ever sees source literals, so
 * a height class built at runtime is a class that was never generated, and the
 * chart collapses to nothing.
 */
function contrastHeight(rows: number): string {
  if (rows <= 4) {
    return "h-64";
  }
  if (rows <= 8) {
    return "h-[26rem]";
  }
  return "h-[34rem]";
}

interface AvailabilityPanelProps {
  locations: FoConsoleAvailabilityLocation[];
  contrast: FoConsoleContrastHour[];
  caveat: string;
  windowCaption: string;
}

function AvailabilityPanel({ locations, contrast, caveat, windowCaption }: AvailabilityPanelProps) {
  if (locations.length === 0 && contrast.length === 0) {
    return (
      <PanelCard title="The platform's own front door" caption={windowCaption}>
        <EmptyState
          icon={<Icon icon={Globe2} size="lg" aria-hidden="true" />}
          size="sm"
          title="No synthetic test result in this window"
          description="Nothing was measuring the front door, or the availability query returned no row. This is the control group for every finding on this board, so its absence is worth chasing."
        />
      </PanelCard>
    );
  }

  const failures = locations.reduce((total, row) => total + row.failures, 0);
  const runs = locations.reduce((total, row) => total + row.runs, 0);
  const rows = contrastRows(contrast);

  return (
    <ChartCard<ContrastRow>
      title="The platform's own front door"
      caption={`${formatCount(runs)} synthetic runs from ${formatCount(locations.length)} locations · ${formatCount(failures)} failed · ${windowCaption}`}
      tableTwin={
        rows.length > 0
          ? {
              id: CONTRAST_TWIN_ID,
              title: "The worst hours, as numbers",
              ariaLabel: `What users felt and what the probe measured, per hour, ${windowCaption}`,
              data: rows,
              getRowId: (row) => row.id,
              columns: [
                { id: "hour", header: "Hour", accessor: (row) => row.label },
                {
                  id: "users",
                  header: "Users",
                  align: "end",
                  accessor: (row) => row.users,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.users)}</span>,
                },
                {
                  id: "events",
                  header: "Loads",
                  align: "end",
                  accessor: (row) => row.events,
                  cell: (row) => <span className="tabular-nums">{formatCount(row.events)}</span>,
                },
                {
                  id: "felt",
                  header: "Felt (p95)",
                  align: "end",
                  accessor: (row) => row.user_p95_ms,
                  cell: (row) => (
                    <span className="tabular-nums">{formatDurationMs(row.user_p95_ms)}</span>
                  ),
                },
                {
                  id: "probe",
                  header: "Probe (p50)",
                  align: "end",
                  accessor: (row) => row.probe_p50_ms,
                  cell: (row) => (
                    <span className="text-fg-muted tabular-nums">
                      {formatDurationMs(row.probe_p50_ms)}
                    </span>
                  ),
                },
                {
                  id: "probe_failures",
                  header: "Probe failures",
                  align: "end",
                  accessor: (row) => row.probe_failures,
                  cell: (row) =>
                    row.probe_failures > 0 ? (
                      <Badge tone="danger" variant="outline" size="sm">
                        <Icon icon={AlertTriangle} size="xs" aria-hidden="true" />{" "}
                        {formatCount(row.probe_failures)}
                      </Badge>
                    ) : (
                      <span className="text-fg-muted tabular-nums">0</span>
                    ),
                },
              ],
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {/* The caveat leads the card, above every probe number it qualifies —
            the chart included. It is the panel's own sentence from the payload,
            never a string in this file. */}
        {caveat ? (
          <Alert tone="info" title="What a green result here does and does not prove">
            {caveat}
          </Alert>
        ) : null}

        {rows.length > 0 ? <ContrastChart rows={rows} windowCaption={windowCaption} /> : null}

        {locations.length > 0 ? <LocationTable locations={locations} /> : null}
      </div>
    </ChartCard>
  );
}

/* ------------------------------------------------- felt versus measured --- */

interface ContrastChartProps {
  rows: ContrastRow[];
  windowCaption: string;
}

/**
 * The comparison, on one axis. Paired horizontal bars are the sanctioned form
 * for magnitudes across categories (§2), and one shared linear millisecond
 * scale is the entire argument: the probe bar is short because the probe was
 * fast, not because it is drawn small.
 */
function ContrastChart({ rows, windowCaption }: ContrastChartProps) {
  const failedHours = rows.filter((row) => row.probe_failures > 0);
  const unmeasured = rows.filter((row) => row.probe_p50_ms == null || row.user_p95_ms == null);

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="body-sm" className="font-medium">
        The worst hours for people, beside what the probe measured in the same hour
      </Typography>

      <Chart.Container
        config={CONTRAST_CONFIG}
        className={contrastHeight(rows.length)}
        aria-label={`What users felt and what the synthetic probe measured in the same hour, both on one shared millisecond scale, worst hour first, ${windowCaption}`}
      >
        <Chart.BarChart
          data={rows}
          layout="vertical"
          maxBarSize={18}
          barGap={2}
          barCategoryGap="26%"
          margin={{ top: 4, right: 72, bottom: 0, left: 8 }}
        >
          <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
          {/* One axis for both series. Never two — a second scale would let the
              probe bar be drawn as long as the felt one and destroy the point.
              The domain stays on Recharts' rounded ticks (a scale a reader has
              to trust should read 0 · 25s · 50s, not 0 · 50436 · 100872); the
              right margin is the room the end-of-bar labels draw into. */}
          <Chart.XAxis {...Chart.axisProps} type="number" tickFormatter={formatDurationCompact} />
          <Chart.YAxis {...Chart.axisProps} type="category" dataKey="axis_label" width={140} />
          <Chart.Tooltip
            cursor={{ fill: "var(--norican-surface-hover)" }}
            content={
              <Chart.TooltipContent
                rows={(datum: ContrastRow) => [
                  { label: "Felt by users (p95)", value: formatDurationMs(datum.user_p95_ms) },
                  { label: "Synthetic probe (p50)", value: formatDurationMs(datum.probe_p50_ms) },
                  {
                    label: "Probe runs failed",
                    value:
                      datum.probe_failures > 0 ? formatCount(datum.probe_failures) : "none failed",
                  },
                  { label: "Users", value: formatCount(datum.users) },
                  { label: "Form opens", value: formatCount(datum.events) },
                ]}
              />
            }
          />
          {/* `itemSorter` defaults to sorting the legend alphabetically by the
              raw series key, which would list the probe above the measurement
              it is the control for. Null keeps the series' own order, so the
              legend reads in the order the bars are stacked. */}
          <Chart.Legend content={<Chart.LegendContent />} itemSorter={null} />
          {/* No `LabelList`. Twenty-four printed values over twelve rows is
              exactly §2's "never: a number printed on every data point", and the
              rule admits no exception — the earlier argument that these read as
              "a pair of facts" was special pleading. The chart carries the
              comparison, which is what a reader cannot get any other way, and
              the table twin immediately below carries every number (§4.4). */}
          <Chart.Bar
            {...Chart.seriesDefaults}
            dataKey={FELT_KEY}
            fill={`var(--color-${FELT_KEY})`}
            radius={[0, 3, 3, 0]}
          />
          {/* `colorKey` resolves each bar's fill from the container config by the
              row's own `probe_tone`, so an hour with failed runs is a different
              colour from an hour that was simply fast. `minPointSize` keeps a
              measured-but-tiny probe visible as a sliver; a null probe passes 0
              and stays absent, because absence is not zero. */}
          <Chart.Bar
            {...Chart.seriesDefaults}
            data={rows}
            dataKey={PROBE_KEY}
            colorKey="probe_tone"
            fill={`var(--color-${PROBE_KEY})`}
            radius={[0, 3, 3, 0]}
            minPointSize={(value: number | null | undefined) => (value ? MIN_BAR_PX : 0)}
          />
        </Chart.BarChart>
      </Chart.Container>

      {failedHours.length > 0 ? (
        <div className="flex items-start gap-2">
          <Icon
            icon={AlertTriangle}
            size="sm"
            className="text-danger-fg mt-0.5 shrink-0"
            aria-hidden="true"
          />
          <Typography variant="caption" tone="muted">
            {`The probe failed in ${formatCount(failedHours.length)} of these ${formatCount(rows.length)} hours (${failedHours
              .map((row) => row.label)
              .join(
                ", ",
              )}). Where it still returned a median that bar is danger-toned, and where ` +
              "it returned none there is no bar at all: a probe that did not answer is not a probe " +
              "that answered quickly, and the number beside it is taken over the runs that did " +
              "come back."}
          </Typography>
        </div>
      ) : null}

      {unmeasured.length > 0 ? (
        <Typography variant="caption" tone="muted">
          {`A measurement is missing for ${formatCount(unmeasured.length)} of these hours (${unmeasured
            .map((row) => row.label)
            .join(", ")}). That bar is absent rather than zero-length: nothing here is ` +
            "interpolated, estimated, or carried over from a previous read."}
        </Typography>
      ) : null}

      <Typography variant="caption" tone="muted">
        {`Both bars share one millisecond scale, so the gap between them is the ratio. ${CONTRAST_RULE}`}
      </Typography>
    </div>
  );
}

/* ----------------------------------------------------- where from ------- */

function LocationTable({ locations }: { locations: FoConsoleAvailabilityLocation[] }) {
  return (
    <div className="flex flex-col gap-2">
      <Typography variant="body-sm" className="font-medium">
        Where it is measured from
      </Typography>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="text-fg-muted pb-2 text-left text-xs">
            The gradient is distance, not health: the row that matters for this fleet is the one
            nearest to it.
          </caption>
          <thead>
            <tr className="text-fg-muted text-left text-xs uppercase">
              <th scope="col" className="py-1 pr-4 font-medium">
                Test
              </th>
              <th scope="col" className="py-1 pr-4 font-medium">
                Location
              </th>
              <th scope="col" className="py-1 pr-4 text-right font-medium">
                p50
              </th>
              <th scope="col" className="py-1 pr-4 text-right font-medium">
                p95
              </th>
              <th scope="col" className="py-1 pr-4 text-right font-medium">
                Runs
              </th>
              <th scope="col" className="py-1 text-right font-medium">
                Failed
              </th>
            </tr>
          </thead>
          <tbody>
            {locations.map((row) => (
              <tr key={`${row.test}-${row.location}`} className="border-border border-t">
                <td className="text-fg-muted py-1 pr-4">{row.test}</td>
                <td className="py-1 pr-4">{row.location}</td>
                <td className="py-1 pr-4 text-right tabular-nums">
                  {formatDurationMs(row.p50_ms)}
                </td>
                <td className="text-fg-muted py-1 pr-4 text-right tabular-nums">
                  {formatDurationMs(row.p95_ms)}
                </td>
                <td className="text-fg-muted py-1 pr-4 text-right tabular-nums">
                  {formatCount(row.runs)}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {row.failures > 0 ? (
                    <Badge tone="danger" variant="outline" size="sm">
                      <Icon icon={AlertTriangle} size="xs" aria-hidden="true" />{" "}
                      {formatCount(row.failures)}
                    </Badge>
                  ) : (
                    <span className="text-fg-muted">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
